import styled from 'styled-components'
import { Box, Grid, Flex, Text, Card } from '../../atoms'

export const ChartContainer = styled(Box).attrs({
  as: 'div',
})<{
  $height?: string | number
  $width?: string | number
  $printHeight?: string | number
}>`
  width: ${({ $width }) =>
    typeof $width === 'number' ? `${$width}px` : $width || '100%'};
  height: ${({ $height }) =>
    typeof $height === 'number' ? `${$height}px` : $height || '400px'};
  position: relative;

  @media (max-width: 768px) {
    height: ${({ $height }) =>
      typeof $height === 'number' ? `${$height * 0.75}px` : '300px'};
  }

  @media print {
    width: 100% !important;
    height: ${({ $printHeight, $height }) => {
      const h = $printHeight || $height
      return typeof h === 'number' ? `${h}px` : h || '500px'
    }} !important;
    page-break-inside: avoid;
  }
`

export const ChartSection = styled(Card)`
  height: 100%;
  overflow: hidden;
  flex: auto;

  @media print {
    width: 100%;
    page-break-inside: avoid;
    border: none !important;
    box-shadow: none !important;
  }
`

export const ChartTitle = styled(Text).attrs({
  variant: 'h3',
  weight: 'semibold',
})`
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const ChartGrid = styled(Grid)<{
  $columns?: number | string
  $gap?: number | string
}>`
  grid-template-columns: ${({ $columns }) =>
    typeof $columns === 'number'
      ? `repeat(${$columns}, 1fr)`
      : $columns || '1fr 1fr'};
  gap: ${({ $gap, theme }) =>
    typeof $gap === 'number' ? `${$gap}px` : $gap || theme.spacing.large};
  margin-bottom: ${({ theme }) => theme.spacing.large};

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }

  @media print {
    grid-template-columns: 1fr;
    gap: 40px;
    page-break-inside: avoid;
  }
`

export const LegendContainer = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.large};
  padding: ${({ theme }) => theme.spacing.large};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.medium};

  @media (max-width: 768px) {
    justify-content: center;
  }
`

export const LegendItem = styled(Flex)`
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
`

export const ColorBlock = styled(Box)<{
  $color: string
  $size?: 'small' | 'medium' | 'large'
}>`
  width: ${({ $size }) => {
    switch ($size) {
      case 'small':
        return '24px'
      case 'large':
        return '48px'
      default:
        return '40px'
    }
  }};
  height: ${({ $size }) => {
    switch ($size) {
      case 'small':
        return '16px'
      case 'large':
        return '24px'
      default:
        return '20px'
    }
  }};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`

export const LegendLabel = styled(Text).attrs({
  variant: 'body2',
})`
  color: ${({ theme }) => theme.colors.text.primary};
`

export const ErrorContainer = styled(Box)<{
  $variant?: 'error' | 'warning' | 'info'
}>`
  padding: ${({ theme }) => theme.spacing.large};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin: ${({ theme }) => `${theme.spacing.large} 0`};
  background: ${({ theme: _theme, $variant = 'error' }) => {
    switch ($variant) {
      case 'warning':
        return '#fff3cd'
      case 'info':
        return '#d1ecf1'
      default:
        return '#fee'
    }
  }};
  color: ${({ theme: _theme, $variant = 'error' }) => {
    switch ($variant) {
      case 'warning':
        return '#856404'
      case 'info':
        return '#0c5460'
      default:
        return '#c33'
    }
  }};
`

export const NotAvailableContainer = styled(Box)`
  text-align: center;
  padding: ${({ theme }) => `${theme.spacing.xlarge} ${theme.spacing.large}`};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.large};
`

export const NotAvailableTitle = styled(Text).attrs({
  variant: 'h3',
  weight: 'semibold',
})`
  margin-bottom: ${({ theme }) => theme.spacing.medium};
`

export const NotAvailableText = styled(Text).attrs({
  variant: 'body1',
})`
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 500px;
  margin: 0 auto;
`

export const PrintWrapper = styled(Box)`
  @media print {
    width: 100% !important;
    max-width: none !important;

    /* Ensure all child elements take full width */
    & > * {
      width: 100% !important;
      max-width: none !important;
    }

    /* Force canvas elements to take full width */
    canvas {
      width: 100% !important;
      max-width: none !important;
    }

    /* Ensure chart containers are properly sized */
    & > div > div {
      width: 100% !important;
    }
  }
`
